import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import { imageFallback } from "../utils/store";
import "../css/main.scss";

const collections = [
  {
    image: "core",
    eyebrow: "Easy shapes for everyday movement",
    title: "Comfort, with a considered profile",
    label: "Shop Core Collection",
    to: "/shop?collection=Arenviko%20Core%20Collection",
  },
  {
    image: "studio",
    eyebrow: "Refined details for sharper dressing",
    title: "Modern form, quietly distinctive",
    label: "Explore Studio Collection",
    to: "/shop?collection=Arenviko%20Studio%20Collection",
  },
];
const differences = [
  {
    title: "Never out of place",
    text: "A considered companion to denim, flannel and your favourite tailoring.",
    alt: "Leather loafers worn with casual trousers",
  },
  {
    title: "Material-led",
    text: "Grain, suede and smooth finishes are selected to suit the character of each shape.",
    alt: "Close view of natural footwear materials and precise stitching",
  },
  {
    title: "Balanced underfoot",
    text: "Cushioning and stable proportions support the pace of an ordinary day.",
    alt: "Low supportive heel and flexible sole construction",
  },
  {
    title: "Easy to combine",
    text: "Versatile colours and clean lines work across relaxed and tailored wardrobes.",
    alt: "Contemporary footwear styled with neutral everyday clothing",
  },
];
const journal = [
  {
    title: "Nightfall after dark",
    text: "A study in Onyx. The Nightfall Derby brings expressive grain, clean seam lines and a confident lightweight sole together.",
    to: "/shopDetails/27",
  },
  {
    title: "Small details. Everyday character.",
    text: "Explore how proportion, material and comfort shape the Arenviko approach in Hong Kong.",
    to: "/about#craft",
  },
  {
    title: "A collection for real routines",
    text: "From a first Oxford to an easy weekend loafer, find footwear that settles naturally into your wardrobe.",
    to: "/shop",
  },
];

export default function Main() {
  return (
    <main id="main" className="home-page">
      <section className="home-hero image-overlay">
        <picture>
          <source
            media="(max-width: 699px)"
            srcSet="/img/editorial/hero-mobile.jpg"
          />
          <img
            className="overlay-image"
            src="/img/editorial/hero.jpg"
            alt="Nightfall Derby shoes in a deep Onyx finish"
            fetchpriority="high"
            width="1200"
            height="800"
          />
        </picture>
        <div className="overlay-copy">
          <p className="eyebrow">Arenviko Studio Collection</p>
          <h1>Nightfall After Dark</h1>
          <p className="hero-description">
            A sculpted wingtip. A confident shade of Onyx.
          </p>
          <Link className="button button--light" to="/shopDetails/27">
            Discover the Nightfall
          </Link>
        </div>
      </section>
      <section
        className="collection-pair container section-space"
        aria-label="Our collections"
      >
        {collections.map((collection, index) => (
          <Link
            key={collection.image}
            to={collection.to}
            className={`collection-tile image-overlay collection-${index}`}
          >
            <img
              className="overlay-image"
              src={`/img/editorial/${collection.image}.jpg`}
              alt={collection.title}
              loading="lazy"
              width="1000"
              height="1000"
              onError={imageFallback}
            />
            <div className="overlay-copy">
              <p className="eyebrow">{collection.eyebrow}</p>
              <h2>{collection.title}</h2>
              <span
                className={`button ${index ? "button--white-outline" : "button--light"}`}
              >
                {collection.label}
              </span>
            </div>
          </Link>
        ))}
      </section>
      <section
        className="home-difference container home-section"
        id="difference"
      >
        <h2 className="section-title">
          The {siteConfig.brandTitle} Difference
        </h2>
        <div className="difference-grid">
          {differences.map((difference, index) => (
            <article key={difference.title}>
              <img
                src={`/img/editorial/difference-${index + 1}.jpg`}
                alt={difference.alt}
                loading="lazy"
                width="600"
                height="750"
                onError={imageFallback}
              />
              <h3>{difference.title}</h3>
              <p>{difference.text}</p>
            </article>
          ))}
        </div>
        <div className="section-cta">
          <Link className="button" to="/about#craft">
            Discover our difference
          </Link>
        </div>
      </section>
      <section className="home-journal container home-section">
        <h2 className="section-title">Arenviko Notes</h2>
        <div className="journal-grid">
          {journal.map((article, index) => (
            <article key={article.title}>
              <Link className="journal-image" to={article.to}>
                <img
                  src={`/img/editorial/journal-${index + 1}.jpg`}
                  alt={article.title}
                  loading="lazy"
                  width="700"
                  height="467"
                  onError={imageFallback}
                />
              </Link>
              <h3>
                <Link to={article.to}>{article.title}</Link>
              </h3>
              <p>{article.text}</p>
            </article>
          ))}
        </div>
        <div className="section-cta">
          <Link className="button" to="/about">
            Explore our approach
          </Link>
        </div>
      </section>
      <section className="home-press container home-section">
        <p>Footwear designed around personal style and considered everyday use.</p>
        <h2>Designed around</h2>
        <div
          className="press-names"
          aria-label="Arenviko design principles"
        >
          {[
            "Daily Comfort",
            "Clean Lines",
            "Reliable Materials",
            "Flexible Soles",
            "Balanced Colour",
            "Thoughtful Fit",
            "Easy Styling",
            "Urban Movement",
          ].map((name) => (
            <span key={name}>{name}</span>
          ))}
        </div>
      </section>
      <section className="home-story image-overlay">
        <img
          className="overlay-image"
          src="/img/editorial/story.jpg"
          alt="The Arenviko team reviewing footwear shapes in Hong Kong"
          loading="lazy"
          width="979"
          height="653"
        />
        <div className="overlay-copy">
          <h2>The Arenviko Point of View</h2>
          <p>Contemporary footwear shaped for movement, versatility and real wardrobes.</p>
          <Link className="button button--light" to="/about">
            Discover Arenviko
          </Link>
        </div>
      </section>
      <section
        className="collection-pair closing-collections container section-space"
        aria-label="Discover your next pair"
      >
        {collections.map((collection, index) => (
          <Link
            key={collection.image}
            to={collection.to}
            className="collection-tile image-overlay"
          >
            <img
              className="overlay-image"
              src={`/img/editorial/closing-${collection.image}.jpg`}
              alt={collection.title}
              loading="lazy"
              width="1000"
              height="1000"
              onError={imageFallback}
            />
            <div className="overlay-copy">
              <span
                className={`button ${index ? "button--white-outline" : "button--light"}`}
              >
                {collection.label}
              </span>
            </div>
          </Link>
        ))}
      </section>
    </main>
  );
}
